import { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Button, useToast } from "../..";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";
import usePipelinesApi, { CardNoteItem } from "../Pipeline/use-pipelines";
import { Container, ObservationCounter } from "./styles";

type CardNotesProps = {
  cardId: number | null | undefined;
  cardUserId: number | null | undefined;
  onClose: () => void;
};

const CardNotes = ({ cardId, cardUserId, onClose }: CardNotesProps) => {
  const OBSERVATION_MAX_LENGTH = 500;
  const [observation, setObservation] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [notes, setNotes] = useState<CardNoteItem[]>([]);
  const [expandedNoteId, setExpandedNoteId] = useState<number | null>(null);
  const [expandableNoteIds, setExpandableNoteIds] = useState<number[]>([]);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState("");
  const [isEditingFocused, setIsEditingFocused] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [noteIdToDelete, setNoteIdToDelete] = useState<number | null>(null);
  const [hasShownObservationLimitToast, setHasShownObservationLimitToast] =
    useState(false);
  const toast = useToast();
  const { user, hasAnyPermission } = useAuth() as AuthContextType;
  const primaryColor = user.plataform.mainColor || "#2624CD";
  const { getCardNotes, createCardNote, editCardNote, deleteCardNote } =
    usePipelinesApi();
  const noteTextRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const isOwnCard = cardUserId != null && cardUserId === user.id;
  const canManageNote =
    user.admin || hasAnyPermission("contactPipeline_edit") || isOwnCard;

  const loadNotes = async () => {
    if (!cardId) {
      setNotes([]);
      return;
    }

    try {
      const response = (await getCardNotes(cardId)) as CardNoteItem[];
      setNotes(response ?? []);
    } catch (error: any) {
      setNotes([]);
      toast?.current?.show({
        severity: "error",
        summary: "Erro",
        detail:
          error?.response?.data?.message ||
          error?.message ||
          "Erro ao buscar observações."
      });
    }
  };

  useEffect(() => {
    loadNotes();
  }, [cardId]);

  useEffect(() => {
    const updateExpandableNotes = () => {
      const nextExpandableIds = notes
        .filter((note) => {
          const element = noteTextRefs.current[note.id];
          if (!element) return false;
          return element.scrollHeight > element.clientHeight + 1;
        })
        .map((note) => note.id);

      setExpandableNoteIds(nextExpandableIds);
    };

    const timeoutId = window.setTimeout(updateExpandableNotes, 0);
    window.addEventListener("resize", updateExpandableNotes);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", updateExpandableNotes);
    };
  }, [notes, editingNoteId]);

  const formatDate = (value?: string | null) => {
    if (!value) return "-";

    return new Date(value)
      .toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
      .replace(",", "");
  };

  const handleLimitToast = (value: string) => {
    if (
      value.length === OBSERVATION_MAX_LENGTH &&
      !hasShownObservationLimitToast
    ) {
      toast?.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: `Você atingiu o limite máximo de ${OBSERVATION_MAX_LENGTH} caracteres.`
      });
      setHasShownObservationLimitToast(true);
      return;
    }

    if (
      value.length < OBSERVATION_MAX_LENGTH &&
      hasShownObservationLimitToast
    ) {
      setHasShownObservationLimitToast(false);
    }
  };

  const handleObservationChange = (value: string) => {
    setObservation(value);
    handleLimitToast(value);
  };

  const handleEditingObservationChange = (value: string) => {
    setEditingNoteValue(value);
    handleLimitToast(value);
  };

  const handleCreateObservation = async () => {
    if (!observation.trim()) {
      toast?.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Não é possível criar uma observação em branco."
      });
      return;
    }

    if (!cardId) {
      toast?.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Card não encontrado para criar a observação."
      });
      return;
    }

    try {
      await createCardNote({
        cardId,
        note: observation.trim()
      });

      toast?.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Observação criada com sucesso."
      });

      setObservation("");
      setIsFocused(false);
      setHasShownObservationLimitToast(false);
      await loadNotes();
    } catch (error: any) {
      toast?.current?.show({
        severity: "error",
        summary: "Erro",
        detail:
          error?.response?.data?.message ||
          error?.message ||
          "Erro ao criar observação."
      });
    }
  };

  const handleCancelCreateObservation = () => {
    setObservation("");
    setIsFocused(false);
    setHasShownObservationLimitToast(false);

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const startEditingNote = (note: CardNoteItem) => {
    setEditingNoteId(note.id);
    setEditingNoteValue(note.note ?? "");
    setExpandedNoteId(note.id);
    setIsEditingFocused(false);
    setHasShownObservationLimitToast(false);
  };

  const handleNoteClick = (
    event: React.MouseEvent<HTMLDivElement>,
    note: CardNoteItem
  ) => {
    const selection = window.getSelection();
    const hasSelectedText = (selection?.toString() ?? "").trim().length > 0;

    if (hasSelectedText) {
      return;
    }

    const isExpandable = expandableNoteIds.includes(note.id);
    const isExpanded = expandedNoteId === note.id;

    if (isExpandable && !isExpanded) {
      setExpandedNoteId(note.id);
      return;
    }

    if (!canManageNote) {
      return;
    }

    startEditingNote(note);
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditingNoteValue("");
    setExpandedNoteId(null);
    setIsEditingFocused(false);
    setHasShownObservationLimitToast(false);
  };

  const handleUpdateNote = async () => {
    if (editingNoteId == null) {
      return;
    }

    if (!editingNoteValue.trim()) {
      toast?.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Não é possível salvar uma observação em branco."
      });
      return;
    }

    try {
      await editCardNote({
        id: editingNoteId,
        note: editingNoteValue.trim()
      });

      toast?.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Observação atualizada com sucesso."
      });

      cancelEditingNote();
      await loadNotes();
    } catch (error: any) {
      toast?.current?.show({
        severity: "error",
        summary: "Erro",
        detail:
          error?.response?.data?.message ||
          error?.message ||
          "Erro ao atualizar observação."
      });
    }
  };

  const handleDeleteNote = async () => {
    if (!noteIdToDelete) {
      return;
    }

    try {
      await deleteCardNote(noteIdToDelete);

      toast?.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Observação excluída com sucesso."
      });

      if (editingNoteId === noteIdToDelete) {
        cancelEditingNote();
      }

      setConfirmDeleteModal(false);
      setNoteIdToDelete(null);
      await loadNotes();
    } catch (error: any) {
      setConfirmDeleteModal(false);
      setNoteIdToDelete(null);
      toast?.current?.show({
        severity: "error",
        summary: "Erro",
        detail:
          error?.response?.data?.message ||
          error?.message ||
          "Erro ao excluir observação."
      });
    }
  };

  const handleNoteBlur = (
    event: React.FocusEvent<HTMLDivElement>,
    noteId: number
  ) => {
    const nextFocused = event.relatedTarget as Node | null;

    if (nextFocused && event.currentTarget.contains(nextFocused)) {
      return;
    }

    if (expandedNoteId === noteId) {
      setExpandedNoteId(null);
    }
  };

  return (
    <Container>
      <div className="create">
        <InputTextarea
          className={`createTextarea ${isFocused ? "expanded" : ""}`}
          value={observation}
          onChange={(e) => handleObservationChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder="Adicionar observação"
          maxLength={OBSERVATION_MAX_LENGTH}
        />

        <div className="bottomCreate">
          {isFocused ? (
            <>
              <button
                type="button"
                onMouseDown={handleCreateObservation}
                style={{
                  background: primaryColor,
                  borderColor: primaryColor,
                  color: "#fff"
                }}
              >
                Adicionar
              </button>

              <button type="button" onMouseDown={handleCancelCreateObservation}>
                Cancelar
              </button>
            </>
          ) : null}

          {isFocused ? (
            <ObservationCounter>{observation.length}/500</ObservationCounter>
          ) : null}
        </div>
      </div>
      <hr />

      <div className="notesList">
        {notes.map((note) => (
          <div
            key={note.id}
            className={`noteItem ${
              expandedNoteId === note.id ? "expanded" : ""
            } ${expandableNoteIds.includes(note.id) ? "collapsible" : ""}`}
          >
            {editingNoteId === note.id ? (
              <div className="edit">
                <InputTextarea
                  className="editTextarea"
                  value={editingNoteValue}
                  onChange={(e) =>
                    handleEditingObservationChange(e.target.value)
                  }
                  onFocus={() => setIsEditingFocused(true)}
                  onBlur={() => setIsEditingFocused(false)}
                  placeholder="Editar observação"
                  maxLength={OBSERVATION_MAX_LENGTH}
                />

                <div className="bottomCreate">
                  {isEditingFocused || editingNoteValue.trim() ? (
                    <>
                      <div className="noteActions">
                        <button
                          type="button"
                          onMouseDown={handleUpdateNote}
                          style={{
                            background: primaryColor,
                            borderColor: primaryColor,
                            color: "#fff"
                          }}
                        >
                          Salvar
                        </button>

                        <button type="button" onMouseDown={cancelEditingNote}>
                          Cancelar
                        </button>
                      </div>

                      <ObservationCounter>
                        {editingNoteValue.length}/500
                      </ObservationCounter>
                    </>
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                <div
                  className="noteHeader"
                  tabIndex={0}
                  onClick={(event) => handleNoteClick(event, note)}
                  onBlur={(event) => handleNoteBlur(event, note.id)}
                >
                  <div
                    className="noteText"
                    ref={(element) => {
                      noteTextRefs.current[note.id] = element;
                    }}
                  >
                    {note.note}
                  </div>

                  <div className="noteActions">
                    {canManageNote ? (
                      <button
                        type="button"
                        className="actionButton delete"
                        onClick={(event) => {
                          event.stopPropagation();
                          setNoteIdToDelete(note.id);
                          setConfirmDeleteModal(true);
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                        aria-label="Excluir observação"
                      >
                        <i className="pi pi-trash" />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="noteMeta">
                  {note.updatedAt && note.updatedByName
                    ? `${note.updatedByName} (Editada) - ${formatDate(
                        note.updatedAt
                      )}`
                    : `${note.createdByName} - ${formatDate(note.createdAt)}`}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <Dialog
        visible={confirmDeleteModal}
        onHide={() => {
          setConfirmDeleteModal(false);
          setNoteIdToDelete(null);
        }}
        draggable={false}
        header="Deseja excluir a anotação?"
        style={{ width: "575px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            Ao excluir a anotação, essa ação não poderá ser desfeita. Tem
            certeza que deseja excluir?
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "15px"
            }}
          >
            <Button
              label="Sim"
              className="p-button-danger"
              onClick={handleDeleteNote}
            />
            <Button
              label="Não"
              className="p-button-secondary"
              onClick={() => {
                setConfirmDeleteModal(false);
                setNoteIdToDelete(null);
              }}
            />
          </div>
        </div>
      </Dialog>

      <div className="footerButton">
        <Button
          onClick={onClose}
          style={{
            background: user.plataform.mainColor
              ? user.plataform.mainColor
              : "#2624CD",
            borderColor: user.plataform.mainColor
              ? user.plataform.mainColor
              : "#2624CD",
            color: "#fff",
            padding: "10px 12px",
            width: "100%"
          }}
        >
          FECHAR
        </Button>
      </div>
    </Container>
  );
};

export default CardNotes;
